import { useState, useEffect } from 'react';
import { promoteAPI } from '../services/api';
import { ArrowUpOnSquareIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

export default function PromoteTenantPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null); // tenant id being promoted
  const [confirmTenant, setConfirmTenant] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 25;

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const res = await promoteAPI.getTenants();
      setTenants(res.data || []);
    } catch (err) {
      setError('Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []);

  const handlePromote = async (tenant) => {
    setConfirmTenant(null);
    setPromoting(tenant.id);
    setResult(null);
    setError('');
    try {
      const res = await promoteAPI.promoteTenant(tenant.id);
      setResult(res.data);
      fetchTenants(); // refresh list
    } catch (err) {
      setError(err.response?.data?.detail || 'Promotion failed');
    } finally {
      setPromoting(null);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap gap-2 items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Promote to Production</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Copy tenant data (users, agents, RAG, configs) from staging to production
          </p>
        </div>
        <button
          onClick={fetchTenants}
          className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded">
          <h3 className="font-semibold text-green-800 dark:text-green-300 mb-2">
            Promoted: {result.tenant_name}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm text-green-700 dark:text-green-400">
            {Object.entries(result.tables_copied || {}).map(([table, count]) => (
              <span key={table}>{table}: {count}</span>
            ))}
          </div>
          {result.vector_data && (
            <p className="text-sm text-green-600 dark:text-green-400 mt-2">
              Vector: {result.vector_data.collections} collections, {result.vector_data.embeddings} embeddings
              {result.vector_data.error && <span className="text-yellow-600"> (warning: {result.vector_data.error})</span>}
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                <th className="hidden md:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Users</th>
                <th className="hidden md:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Agents</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {tenants.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">No tenants found</td>
                </tr>
              ) : (
                tenants.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE).map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{t.name}</td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{t.slug}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
                        {t.subscription_tier}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-300">{t.user_count}</td>
                    <td className="hidden md:table-cell px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-300">{t.agent_count}</td>
                    <td className="px-4 py-3 text-sm">
                      {t.is_promoted ? (
                        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircleIcon className="h-4 w-4" />
                          {new Date(t.promoted_at).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-gray-400">
                          <ClockIcon className="h-4 w-4" />
                          Not promoted
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setConfirmTenant(t)}
                        disabled={promoting === t.id}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded ${
                          promoting === t.id
                            ? 'bg-gray-200 text-gray-400 cursor-wait'
                            : t.is_promoted
                            ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/40 dark:text-orange-300'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}
                      >
                        {promoting === t.id ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current" />
                            Promoting...
                          </>
                        ) : (
                          <>
                            <ArrowUpOnSquareIcon className="h-4 w-4" />
                            {t.is_promoted ? 'Re-promote' : 'Promote to Prod'}
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {tenants.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, tenants.length)} of {tenants.length}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => p - 1)}
              disabled={page === 0}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * PAGE_SIZE >= tenants.length}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="font-semibold text-gray-900 dark:text-white text-lg mb-2">
              {confirmTenant.is_promoted ? 'Re-promote' : 'Promote'} to Production?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              <strong>{confirmTenant.name}</strong> ({confirmTenant.slug})
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              This will copy all tenant data to production including:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 mb-6 space-y-1 ml-4 list-disc">
              <li>{confirmTenant.user_count} user(s) with credentials</li>
              <li>{confirmTenant.agent_count} agent(s) with configs</li>
              <li>RAG collections and embeddings</li>
              <li>Notification settings</li>
              <li>License and integrations</li>
            </ul>
            {confirmTenant.is_promoted && (
              <p className="text-sm text-orange-600 dark:text-orange-400 mb-4">
                This tenant was previously promoted. Re-promoting will overwrite production data.
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmTenant(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePromote(confirmTenant)}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                {confirmTenant.is_promoted ? 'Re-promote' : 'Promote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
